// @ts-ignore legacy-hubspot-bender-context is a virtual, undeclared module
import { bender } from 'legacy-hubspot-bender-context';
import { useTheme, isCanvasTheme } from 'foundations-theming/utils';
import { Theme } from 'foundations-theming';
import illustrationsLogosByThemeMetadata from './illustrations-logos-by-theme-metadata';

type AvailableThemes = 'canvas' | 'trellis';

const { staticDomainPrefix } = bender as { staticDomainPrefix: string };

/**
 * Checks if an image exists for a given theme.
 * @param image - The image name (without suffix or extension)
 * @param theme - The theme to check ('canvas' or 'trellis')
 * @returns True if the image exists, false otherwise
 */
const imageExists = (image: string, theme: AvailableThemes): boolean => {
  const illustrationsLogosByTheme = illustrationsLogosByThemeMetadata[
    theme
  ] as readonly string[];
  return illustrationsLogosByTheme.includes(image);
};

type GetImageUrlType = (
  name: string,
  extension?: string,
  isIE11?: boolean,
  theme?: Theme
) => string;

/**
 * Returns the URL of an image for a given theme. If the image does not exist for any theme, the URL of the empty state chart image is returned.
 * @param name - The name of the image to get the URL for
 * @param extension - The extension of the image to get the URL for
 * @param isIE11 - Whether the image is for IE11
 * @param theme - The theme (object) of the image to get the URL for (used by the useGetImageUrl hook)
 * @returns The URL of the image
 */
export const getImageUrl: GetImageUrlType = (
  name,
  extension = '.svg',
  isIE11 = false,
  theme
) => {
  const suffix = isIE11 ? '-ie11' : '';
  const img = `${name}${suffix}${extension}`;

  // Determine preferred theme order
  const preferredTheme: AvailableThemes =
    !theme || isCanvasTheme(theme) ? 'canvas' : 'trellis';
  const oppositeTheme: AvailableThemes =
    preferredTheme === 'canvas' ? 'trellis' : 'canvas';

  // Try preferred theme first, then the opposite/fallback theme
  const themesToTry: AvailableThemes[] = [preferredTheme, oppositeTheme];
  for (const themeToTry of themesToTry) {
    if (imageExists(name, themeToTry)) {
      return `${staticDomainPrefix}/ui-images/static-2.1269/optimized/${themeToTry}/${img}`;
    }
  }

  return `${staticDomainPrefix}/ui-images/static-2.1269/optimized/canvas/empty-state-charts${suffix}${extension}`;
};

/**
 * Custom hook that is a theme-aware version of getImageUrl.
 * To be used by the Illustration component to allow for correct image to be loaded based on the theme.
 *
 * @example
 * ```tsx
 * const imageUrl = useGetImageUrl('account-security', undefined);
 * return (<Image src={imageUrl} />);
 * ```
 */
export const useGetImageUrl: GetImageUrlType = (name, extension, isIE11) => {
  const theme = useTheme();

  return getImageUrl(name, extension, isIE11, theme);
};
